import classNames from 'classnames';
import { useI18n } from '@/libraries/contexts/i18n-context';
import React from 'react';
import Typography from '@/components/atoms/typography/typography';
import Button from '@/components/molecules/button/button';
import styles from './quantity-selector.module.scss';

export interface QuantitySelectorProps {
  /** The class name to apply */
  className?: string;
  /** The text to show as description */
  description: string;
  /** The text to show as label */
  label: string;
  /** Maximum quantity for the quantity selector */
  maximumQuantity?: number;
  /** Minimum quantity for the quantity selector */
  minimumQuantity?: number;
  /** An event that is triggered on click decrement */
  onClickDecrement?: () => void;
  /** An event that is triggered on click increment*/
  onClickIncrement?: () => void;
  /** Value displayed in the quantity selector */
  value: number;
}

const QuantitySelector = ({
  className,
  description,
  label,
  maximumQuantity = Infinity,
  minimumQuantity = 0,
  onClickDecrement,
  onClickIncrement,
  value = 0,
}: QuantitySelectorProps) => {
  const { t } = useI18n();

  return (
    <div
      aria-label={label}
      className={classNames(styles.component, className)}
      role="group"
    >
      <div className={styles.labelContainer}>
        <Typography
          className={styles.label}
          tag="label"
          tagStyle="bodyMediumBold"
        >
          {label}
        </Typography>

        {description && (
          <Typography
            className={styles.description}
            tag="p"
            tagStyle="bodyMedium"
          >
            {description}
          </Typography>
        )}
      </div>

      <div className={styles.buttonContainer}>
        <Button
          aria-disabled={value <= minimumQuantity}
          aria-label={t('items.common.words.decreaseQuantity')}
          disabled={value <= minimumQuantity}
          icon="Minus"
          onClick={onClickDecrement}
          variant="tertiary"
        />

        <Typography
          aria-atomic="true"
          aria-live="polite"
          aria-relevant="all"
          className={styles.quantity}
          tag="label"
          tagStyle="bodyMedium"
        >
          {value}
        </Typography>

        <Button
          aria-disabled={value >= maximumQuantity}
          aria-label={t('items.common.words.increaseQuantity')}
          disabled={value >= maximumQuantity}
          icon="Plus"
          onClick={onClickIncrement}
          variant="tertiary"
        />
      </div>
    </div>
  );
};

QuantitySelector.displayName = 'QuantitySelector';

export default QuantitySelector;
